import { useEffect, useMemo, useState } from "react";
import * as Schema from "effect/Schema";
import { useThreadShells } from "../state/entities";
import { useRootSessionThreadInputs } from "../rootSessionStripContainer";
import { useLocalStorage } from "../hooks/useLocalStorage";
import type { WorldAttentionNotice } from "./notifications";
import type { NotificationRead } from "./notificationObserver";

const LocalReads = Schema.Record(
  Schema.String,
  Schema.Struct({
    revision: Schema.String,
    state: Schema.Literals(["read", "dismissed", "unread"]),
  }),
);
const empty: typeof LocalReads.Type = {};
export function useNotificationActivity(notices: WorldAttentionNotice[]) {
  const shells = useThreadShells();
  const threads = useRootSessionThreadInputs();
  const [localReads, setLocalReads] = useLocalStorage(
    "cleo:notifications:local-reads:v1",
    empty,
    LocalReads,
  );
  const fingerprint = JSON.stringify(
    notices.map((notice) => {
      const id = notice.key.slice(notice.key.lastIndexOf(":") + 1);
      const relevant = shells.filter(
        (shell) =>
          (shell.worldKey ?? null) === notice.worldKey &&
          (id === "finished"
            ? !!shell.settledAt
            : threads.some(
                (thread) =>
                  thread.environmentId === shell.environmentId &&
                  thread.threadId === shell.id &&
                  thread.status === id &&
                  thread.archivedAt === null &&
                  thread.snoozedUntil === null &&
                  thread.settledAt === null,
              )),
      );
      return {
        key: notice.key,
        parts: relevant
          .map((shell) => [
            shell.environmentId,
            shell.id,
            id === "finished"
              ? shell.settledAt
              : [shell.latestTurn, shell.session?.updatedAt ?? null],
          ])
          .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
      };
    }),
  );
  const [hashed, setHashed] = useState<{
    fingerprint: string;
    revisions: Map<string, string>;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      (JSON.parse(fingerprint) as { key: string; parts: unknown }[]).map(async (row) => {
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(JSON.stringify(row.parts)),
        );
        return [
          row.key,
          Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join(""),
        ] as const;
      }),
    ).then((rows) => {
      if (!cancelled) setHashed({ fingerprint, revisions: new Map(rows) });
    });
    return () => {
      cancelled = true;
    };
  }, [fingerprint]);
  const rows = useMemo(
    () =>
      hashed?.fingerprint !== fingerprint
        ? []
        : notices.map((notice) => ({
            ...notice,
            id: notice.key.slice(notice.key.lastIndexOf(":") + 1),
            revision: hashed.revisions.get(notice.key)!,
          })),
    [notices, fingerprint, hashed],
  );
  return {
    rows,
    localReads,
    markLocal: (rows: NotificationRead[]) =>
      setLocalReads((prior) => {
        const next = { ...prior };
        for (const row of rows) next[row.id] = { revision: row.revision, state: row.state };
        return next;
      }),
  };
}
